import React, { ComponentProps, forwardRef, useEffect, useMemo } from 'react'
import { useDebounce } from 'react-use'
import uniqBy from 'lodash/uniqBy'
import { ILocationSearchQuery, useLocationSearch } from 'transfix-maps/hooks/useLocationSearch'
import AutocompleteBase from 'transfix-ui/components/AutocompleteBase'
import {
  TInputChangeHandler,
  useAutocompleteInputState,
  useAutocompleteLazyQuery,
} from 'transfix-ui/components/AutocompleteBase/hooks'
import {
  IAutocompleteBaseOption,
  TAutocompleteBaseChangeHandler,
} from 'transfix-ui/components/AutocompleteBase/types'
import { isAutocompleteBaseOption } from 'transfix-ui/components/AutocompleteBase/utils/typeGuards'

const getComputedValue = (
  options: IAutocompleteBaseOption[],
  value: string | string[] | null
): IAutocompleteBaseOption | IAutocompleteBaseOption[] | null => {
  if (!value) return null

  if (typeof value === 'string') {
    const option = options.find(({ text }) => text === value)
    return option || null
  }

  return options.filter(option => value.includes(option.text || ''))
}

export interface IAutocompleteLocationProps
  extends Omit<
    ComponentProps<typeof AutocompleteBase>,
    'value' | 'onChange' | 'options' | 'loading'
  > {
  value: string | string[] | null
  required?: boolean
  onChange: (newVal: string | string[] | null) => void
  inputError?: boolean
  inputValidationMessage?: string
  dataTest?: string
  filterOption?: (props: ILocationSearchQuery['geocodingSearch'][number]) => boolean
  mapOption?: (
    props: ILocationSearchQuery['geocodingSearch'][number]
  ) => Omit<IAutocompleteBaseOption, 'groupIndex'>
  transform?: (v: IAutocompleteBaseOption) => string
}

export const AutocompleteLocation = forwardRef<HTMLInputElement, IAutocompleteLocationProps>(
  (
    {
      clearOnBlur = false,
      dataTest = 'AutocompleteLocation',
      inputError,
      inputIcon = 'LocationPin',
      inputValidationMessage,
      label = 'Location',
      onChange,
      placeholder = 'Search for a location',
      renderInputType = 'withIcon',
      required,
      size = 'small',
      value: valueProp,
      filterOption = () => true,
      mapOption = ({
        city,
        country,
        id,
        state,
      }: ILocationSearchQuery['geocodingSearch'][number]) => {
        const placeName = [city, state, country].filter(Boolean).join(', ')
        return {
          id,
          text: placeName || id || '',
        }
      },
      multiple,
      transform = ({ text }) => text || '',
      ...props
    },
    ref
  ) => {
    const [inputValue, setInputValue] = useAutocompleteInputState('')
    const [refetch, { options = [], loading }] = useAutocompleteLazyQuery(
      useLocationSearch,
      {
        variables: { searchString: inputValue },
        fetchPolicy: 'network-only',
      },
      {
        value: valueProp || null,
        getOptionsConfig: data => {
          const { geocodingSearch = [] } = data || {}
          return {
            groupIndex: 1,
            options: [
              ...geocodingSearch.filter(filterOption),
              ...(Array.isArray(valueProp)
                ? valueProp.map(state => ({
                    id: state,
                    text: state,
                    value: state,
                  }))
                : []),
            ],
            mapOption,
          }
        },
      }
    )

    /**
     * Refetch when the input value changes
     */
    useDebounce(
      () => {
        if (inputValue) {
          refetch({ variables: { searchString: inputValue } })
        }
      },
      250,
      [inputValue]
    )

    const changeHandler: TAutocompleteBaseChangeHandler = (_, newVal) => {
      if (Array.isArray(newVal)) {
        onChange(newVal.map(val => (typeof val === 'string' ? val : transform(val))))
      } else if (isAutocompleteBaseOption(newVal)) {
        onChange(transform(newVal))
      } else {
        onChange(null)
      }
    }

    const inputChangeHandler: TInputChangeHandler = (e, newVal) => {
      /**
       * MUI triggers an onChange event with `null` value when
       * trying to press delete/backspace when there's a value
       *
       * Because this is an invalid event, we ignore it
       */
      if (e) {
        setInputValue(e, newVal)
      }
    }

    /**
     * Set input value by default
     */
    useEffect(() => {
      if (!inputValue && typeof valueProp === 'string') {
        setInputValue({} as any, valueProp || '')
      }
    }, [inputValue, setInputValue, valueProp])

    const value = getComputedValue(options, valueProp) || (multiple ? [] : null)
    const uniqueOptions = useMemo(() => uniqBy(options, 'text'), [options])

    return (
      <AutocompleteBase
        {...props}
        label={label}
        multiple={multiple}
        data-test={dataTest}
        ref={ref}
        value={value}
        onChange={changeHandler}
        inputValue={inputValue}
        onInputChange={inputChangeHandler}
        options={uniqueOptions}
        loading={loading}
        placeholder={placeholder}
        loadingText='Loading...'
        selectOnFocus
        autoSelect
        clearOnBlur={clearOnBlur}
        renderInputType={renderInputType}
        inputIcon={inputIcon}
        size={size}
        required={required}
        inputError={inputError}
        inputValidationMessage={inputValidationMessage}
      />
    )
  }
)

export default AutocompleteLocation
